import * as React from "react";
import { toast } from "react-toastify";
import { useToggle } from "react-use";
import {
  useCreateSnapshotPivotMutation,
  useDeleteSnapshotPivotMutation,
  useGetPivotFieldSchemaQuery,
  useGetSnapshotPivotsQuery,
  useNormalisePivotFieldMutation,
} from "@/modules/snapshots/businessReports.api";
import type { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { buildPivotResult, getPivotSubtitle, getPivotTitle } from "@/modules/snapshots/grid/pivots/pivotUtils";
import type { PivotConfig, PivotResult, SavedPivot } from "@/modules/snapshots/grid/pivots/pivotTypes";
import { PAGE_SIZE } from "@/modules/snapshots/grid/constants";
import type { SnapshotGridTab } from "@/modules/snapshots/grid/types";

interface Input {
  collectionId: string | undefined;
  subcollectionId: string | undefined;
  snapshotCompleted: BusinessReportOutcome[];
  isGridResultStale: boolean;
  totalGridRows: number;
}

/** Table/dashboard tab plus saved pivots, which need every matching row loaded to be computed client-side. */
export const useSnapshotGridDashboard = ({
  collectionId,
  subcollectionId,
  snapshotCompleted,
  isGridResultStale,
  totalGridRows,
}: Input) => {
  const [activeTab, setActiveTab] = React.useState<SnapshotGridTab>("table");
  const [isPivotBuilderOpen, togglePivotBuilderOpen] = useToggle(false);
  const [normalisePivotField] = useNormalisePivotFieldMutation();
  const [createSnapshotPivot] = useCreateSnapshotPivotMutation();
  const [deleteSnapshotPivot] = useDeleteSnapshotPivotMutation();
  const pivotFieldSchemaQuery = useGetPivotFieldSchemaQuery(collectionId ?? "", { skip: !collectionId });
  const savedPivotsQuery = useGetSnapshotPivotsQuery(collectionId ?? "", { skip: !collectionId });

  React.useEffect(() => {
    togglePivotBuilderOpen(false);
    setActiveTab("table");
  }, [collectionId, subcollectionId, togglePivotBuilderOpen]);

  const isPivotDataComplete = !isGridResultStale && totalGridRows === snapshotCompleted.length;
  const savedPivotCount = savedPivotsQuery.data?.length ?? 0;
  const pivotUnavailableMessage = `Pivot summaries need the full filtered result set. This view currently has ${totalGridRows.toLocaleString()} matching snapshots and loads ${PAGE_SIZE.toLocaleString()} at a time.`;

  const savedPivots = React.useMemo<SavedPivot[]>(() => {
    // Pivots are only rendered on the dashboard tab; skip rebuilding them on every table page merge or cell edit.
    if (!isPivotDataComplete || activeTab !== "dashboard") return [];

    return (savedPivotsQuery.data ?? []).map(pivot => {
      const result = buildPivotResult({
        outcomes: snapshotCompleted,
        rowFieldKey: pivot.config.rowFieldKey,
        rowFieldType: pivot.config.rowFieldType,
        valueFields: pivot.config.valueFields,
        chartType: pivot.config.chartType,
        seriesFieldKey: pivot.config.seriesFieldKey,
        seriesFieldLabel: pivot.config.seriesFieldLabel,
        datePeriod: pivot.config.datePeriod,
        normalisationMapping: pivot.config.normalisation?.mapping,
      });

      return {
        id: pivot.id,
        title: pivot.title,
        subtitle: getPivotSubtitle(result),
        config: pivot.config,
        result,
      };
    });
  }, [activeTab, isPivotDataComplete, savedPivotsQuery.data, snapshotCompleted]);

  React.useEffect(() => {
    if (!isPivotDataComplete && isPivotBuilderOpen) {
      togglePivotBuilderOpen(false);
    }
  }, [isPivotBuilderOpen, isPivotDataComplete, togglePivotBuilderOpen]);

  const handleSavePivot = React.useCallback(
    async (config: PivotConfig, result: PivotResult) => {
      if (!collectionId) {
        toast.error("Failed to save pivot");
        throw new Error("Missing snapshot collection id");
      }

      try {
        await createSnapshotPivot({
          snapshotCollectionId: collectionId,
          title: getPivotTitle(config),
          subtitle: getPivotSubtitle(result),
          config,
        }).unwrap();
        setActiveTab("dashboard");
        toast.success("Pivot saved");
      } catch (error) {
        toast.error("Failed to save pivot");
        throw error;
      }
    },
    [collectionId, createSnapshotPivot],
  );

  const handleDeletePivot = React.useCallback(
    async (id: string) => {
      if (!collectionId) {
        toast.error("Failed to delete pivot");
        return;
      }

      try {
        await deleteSnapshotPivot({ snapshotCollectionId: collectionId, pivotId: id }).unwrap();
        toast.success("Pivot deleted");
      } catch {
        toast.error("Failed to delete pivot");
      }
    },
    [collectionId, deleteSnapshotPivot],
  );

  const handleNormalisePivotField = React.useCallback(
    async (field: string, rows: Array<{ id: string; value: string }>) => {
      const response = await normalisePivotField({ field, rows }).unwrap();
      return response.mapping;
    },
    [normalisePivotField],
  );

  return {
    activeTab,
    setActiveTab,
    isPivotBuilderOpen,
    togglePivotBuilderOpen,
    isPivotDataComplete,
    pivotUnavailableMessage,
    savedPivots,
    savedPivotCount,
    isLoadingSavedPivots: savedPivotsQuery.isFetching,
    pivotFieldSchema: pivotFieldSchemaQuery.data?.fields,
    handleSavePivot,
    handleDeletePivot,
    handleNormalisePivotField,
  };
};
